export type MetricStatus = 'good' | 'ok' | 'warn' | 'bad' | 'idle' | 'na';
export type MetricDirection = 'higher-better' | 'band';

/**
 * higher-better (completion / break-even pace) — preserves legacy thresholds:
 *   ≤30 bad · ≤60 ok (was "orange") · ≤80 warn · >80 good
 * Visual order: bad → orange-ish (ok) → yellow-ish (warn) → green (good).
 */
export function statusForPct(pct: number, direction: MetricDirection): MetricStatus {
  if (!Number.isFinite(pct) || pct < 0) return 'na';

  if (direction === 'higher-better') {
    if (pct <= 30) return 'bad';
    if (pct <= 60) return 'ok';
    if (pct <= 80) return 'warn';
    return 'good';
  }

  // band — workload vs the 160h monthly cap
  if (pct < 60) return 'idle';
  if (pct <= 100) return 'good';
  if (pct <= 110) return 'warn';
  return 'bad';
}

/** Russian status labels — single source for aria/title text. */
export const STATUS_LABELS_RU: Record<MetricStatus, string> = {
  good: 'в норме',
  ok: 'немного отстаём',
  warn: 'на пределе',
  bad: 'перегрузка',
  idle: 'есть запас',
  na: 'нет данных',
};

/** Band-specific labels (override generic where wording differs). */
export const BAND_STATUS_LABELS_RU: Record<MetricStatus, string> = {
  ...STATUS_LABELS_RU,
  warn: 'на пределе',
  bad: 'перегрузка',
};

/** higher-better pace labels for break-even / health surfaces. */
export const PACE_STATUS_LABELS_RU: Partial<Record<MetricStatus, string>> = {
  good: 'В темпе безубыточности',
  ok: 'Отстаём от плана',
  warn: 'Немного отстаём',
  bad: 'Сильно отстаём — нужно ускориться',
};

export function statusLabelRu(
  status: MetricStatus,
  direction: MetricDirection = 'band',
): string {
  if (direction === 'higher-better' && status in PACE_STATUS_LABELS_RU) {
    return PACE_STATUS_LABELS_RU[status] ?? STATUS_LABELS_RU[status];
  }
  if (direction === 'band') {
    return BAND_STATUS_LABELS_RU[status];
  }
  return STATUS_LABELS_RU[status];
}

/** CSS custom-property reference for a status colour. */
export function statusColorVar(status: MetricStatus): string {
  if (status === 'na') return 'var(--muted)';
  return `var(--chart-status-${status})`;
}

/** Kebab-case BEM modifier suffix, e.g. `metric-bar--good`. */
export function statusModifier(status: MetricStatus): string {
  return status;
}
